import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import uniqWith from 'lodash/uniqWith';
import { createSelector } from 'reselect';

import {
  BORE_LEVEL_PRECISION,
  DAM_PRECISION,
  INTERMEDIARY_PRECISION,
  LINE_PRESSURE_PRECISION,
  PIPE_VOLUME_PER_TICK_PRECISION,
  TANK_LEVEL_PRECISION,
  TANK_VOLUME_PRECISION,
  TROUGH_DIRECT_DEFAULT_HEIGHT_CM,
  TROUGH_DIRECT_PRECISION
} from 'constants/samplePoint';
import { selectDevices } from 'store/modules/devices/selectors';
import { selectSamplePointsStatistics } from 'store/modules/samplePointsStatistics/selectors';
import { selectSites } from 'store/modules/sites/selectors';
import selectTime from 'store/modules/time/selectors';
import { ApplicationRootState } from 'store/types';
import { LatLong } from 'types/asset';
import { AssetTypeCode, AssetTypeId, RawAssetTypeCode } from 'types/models/asset-type';
import Device from 'types/models/device';
import { SamplePointWithHeaterAddOn } from 'types/models/heater';
import SamplePoint, {
  DamSamplePoint,
  LevelDisplayUnit,
  MachineControl,
  SamplePointId,
  SamplePointSid,
  SamplePointWithNonNullableLocation,
  SensorTagId
} from 'types/models/samplePoint';
import {
  MachineControlStatistic,
  MergedSamplePointStatistic,
  SamplePointStatisticWithAggregates
} from 'types/models/samplePointsStatistic';
import { SiteId } from 'types/models/site';
import { DataType } from 'types/sample.enum';
import { calculateTankVolumeL } from 'utils/calculate-tank-volume-height';
import convertMetricToImperial, {
  convertMetricToImperialAlwaysRounding
} from 'utils/convert-metric-to-imperial';
import convertUnit, { Quantity, Reading } from 'utils/convert-unit';
import createDeepEqualSelector from 'utils/create-deep-equal-selector';
import { getDeviceLocation } from 'utils/get-location';
import getSampleVolumePerMinute from 'utils/get-sample-volume-per-minute';
import { UnitLevelMd, UnitLevelSm, UnitType, getUnitSymbolByCountry } from 'utils/get-unit-by-country';
import isSamplePointAlwaysConfigured from 'utils/is-sample-point-always-configured';
import percentage from 'utils/Math/percentage';
import roundNumber from 'utils/round-number';
import getBoreDepthLevelDisplay from 'utils/Sample/get-bore-depth-level-display';
import { getDamLevel } from 'utils/Sample/get-dam-level';
import { getTankLevelForecast } from 'utils/Sample/get-tank-level-forecast';
import { getTankChangePerHour } from 'utils/SamplePoints/get-tank-change-per-hour';
import { getHeaterStatus } from 'utils/SamplePoints/heater/get-heater-status';
import { isTroughDirect, isWirelessTrough } from 'utils/SamplePoints/trough';
import { assertIsPowerMeterStatistic } from 'utils/SamplePointStatistics/assert-is-power-meter-statistic';
import { getCurrentLowHighSummary } from 'utils/SamplePointStatistics/get-current-low-high-summary';
import { getRainSummary } from 'utils/SamplePointStatistics/get-rain-summary';
import { convertLitresTo } from 'utils/unit-conversions';

import { computeBridgeDevice } from './utils/compute-bridge-device';
import { computePowerMeterSummaryMetrics } from './utils/compute-power-meter-summary-metrics';
import { hasHeaterHost } from './utils/find-heater-host';
import { findNestedWeatherSensors } from './utils/find-nested-weather-sensors';
import { retrieveWeatherSummaryMetrics } from './utils/retrieve-weather-summary-metrics';
import {
  AutomaticControlType,
  MachineControlStatus
} from '../controlPoints/types';
import { selectCurrentEnterpriseCountry } from '../enterprise/selectors';
import { selectDefaultSite } from '../localUserSettings/selectors';

const selectSamplePointsState = (state: ApplicationRootState) =>
  state.samplePoints;

/** Locale sample point config and statistics */
const selectSamplePoints = createDeepEqualSelector(
  [
    selectSamplePointsState,
    selectSites,
    selectSamplePointsStatistics,
    selectDevices,
    selectTime,
    selectCurrentEnterpriseCountry
  ],
  (
    samplePointsState,
    sites,
    samplePointsStatistics,
    devices,
    currentDateInSeconds,
    country = 'AUS'
  ): Record<SamplePointId, SamplePoint> =>
    mapValues(samplePointsState, (samplePoint) => {
      const isUS = country === 'USA';
      const { id, siteId, assetTypeId, deviceId } = samplePoint;
      const site = sites[siteId];
      const siteName = site?.name;
      const siteTimezoneCode = site?.timezoneCode;
      const device: Device | undefined = devices[deviceId];

      const selectedSamplePoint: SamplePoint = {
        ...samplePoint,
        // Don't remove the following line. It's for deep copy.
        config: {
          ...samplePoint.config
        },
        isConfigured:
          samplePoint.isConfigured ||
          isSamplePointAlwaysConfigured(samplePoint),
        siteName,
        siteTimezoneCode,
        device: pick(device, [
          'id',
          'name',
          'backhaulType',
          'model',
          'status',
          'lastStatusDate',
          'lastDataDate',
          'statusCause',
          'serialNumber',
          'batteryLevel',
          'isBatteryLow'
        ]),
        bridgeDevice: computeBridgeDevice(samplePoint, device)
      };

      // ============================ COMPUTE LOCATION, must be after computeBridgeDevice ============================
      const hubLocation = device ? getDeviceLocation(device) : undefined;
      const bridgeLocation = selectedSamplePoint.bridgeDevice?.location;
      const coordinates = bridgeLocation || hubLocation || undefined;
      const location: SamplePoint['location'] = coordinates
        ? { coordinates, isBridgeDeviceLocation: !!bridgeLocation }
        : undefined;
      selectedSamplePoint.location = location;

      // ============================== CONVERT OFFSET ==============================
      // Currently, wind is the only sensor that supports offset not in length units
      if (selectedSamplePoint.config.offset && selectedSamplePoint.assetTypeId !== AssetTypeCode.WIND) {
        selectedSamplePoint.config.offset = convertMetricToImperial(
          UnitType.LEVEL_SM,
          country,
          selectedSamplePoint.config.offset
        );
      }

      // ============================== INITIALIZE AND CONVERT PIPE'S VOLUME PER TICK ==============================
      if (assetTypeId === AssetTypeCode.PIPE) {
        const isGasPipe = samplePoint.sensorTags.id === SensorTagId.GAS_FLOW_METER;
        const volumeUnitType = isGasPipe ? UnitType.VOLUME_GAS : UnitType.VOLUME_SM;
        selectedSamplePoint.config.tickConverter = convertMetricToImperialAlwaysRounding(
          volumeUnitType,
          country,
          selectedSamplePoint.config.tickConverter,
          PIPE_VOLUME_PER_TICK_PRECISION
        );
      }

      // ============================== CALCULATE LAST SAMPLE DATE ==============================
      let assetLastSampleDate: number | undefined;

      const samplePointsStatistic = samplePointsStatistics[id];

      const lastSample = samplePointsStatistic?.lastSample;
      const lastSampleDate = lastSample?.date || samplePoint.lastSampleDate;

      // Store the most recent date of a sample point for tracking if data needs
      // to be re-requested
      if (
        !assetLastSampleDate ||
        (lastSampleDate && lastSampleDate > assetLastSampleDate)
      ) {
        assetLastSampleDate = lastSampleDate;
      }
      selectedSamplePoint.lastSampleDate = assetLastSampleDate;

      // ============================== COMPUTE STATISTICS PER ASSET TYPE ==============================
      const levelSmSymbol: UnitLevelSm = getUnitSymbolByCountry<UnitLevelSm>({
        unitType: UnitType.LEVEL_SM,
        country
      });
      if (lastSample && assetTypeId === AssetTypeCode.WATER_TANK) {
        const localeMaxValue = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          samplePoint.config?.maxValue,
          TANK_LEVEL_PRECISION
        ) || 0;
        const localeTotalCapacity = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          samplePoint.config?.totalCapacity,
          TANK_VOLUME_PRECISION
        );
        const forecastedLevel = currentDateInSeconds ? getTankLevelForecast(
          lastSample,
          currentDateInSeconds,
          samplePoint.config?.maxValue,
          country
        ) : undefined;
        const forecastedVolumeL: number | undefined = !isNil(forecastedLevel) ? calculateTankVolumeL(
          forecastedLevel.metric.value,
          samplePoint.config?.maxValue,
          samplePoint.config?.totalCapacity,
          samplePoint.config,
          { decimalPlaces: INTERMEDIARY_PRECISION }
        ) : undefined;
        const localeForecastedVolume = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          forecastedVolumeL,
          TANK_VOLUME_PRECISION
        );
        let forecastedPercentFull;
        if (samplePoint.config.totalCapacity) {
          forecastedPercentFull = percentage(
            forecastedVolumeL,
            samplePoint.config.totalCapacity
          );
        } else {
          forecastedPercentFull = percentage(
            forecastedLevel?.metric.value,
            samplePoint.config.maxValue
          );
        }
        selectedSamplePoint.config.maxValue = localeMaxValue;
        selectedSamplePoint.config.totalCapacity = localeTotalCapacity;
        if (samplePoint.config?.tankLength) {
          selectedSamplePoint.config.tankLength = convertMetricToImperial(
            UnitType.LEVEL_SM,
            country,
            samplePoint.config?.tankLength
          );
        }
        if (samplePoint.config?.tankWidth) {
          selectedSamplePoint.config.tankWidth = convertMetricToImperial(
            UnitType.LEVEL_SM,
            country,
            samplePoint.config?.tankWidth
          );
        }

        selectedSamplePoint.waterLevelStatistics = {
          changePerHour: getTankChangePerHour(lastSample, country),
          forecastedValue: forecastedLevel?.locale.value,
          forecastedPercentFull,
          maxValue: localeMaxValue,
          forecastedVolume: localeForecastedVolume,
          maxVolume: localeTotalCapacity
        };
      }
      if (lastSample && assetTypeId === AssetTypeCode.FUEL_TANK) {
        const heightLocale: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          samplePoint.config?.maxValue,
          TANK_LEVEL_PRECISION
        ) || 0;
        const maxVolumeLocale: number | undefined = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          samplePoint.config?.totalCapacity,
          TANK_VOLUME_PRECISION
        );
        const levelActualLocale: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          lastSample.rwValue,
          TANK_LEVEL_PRECISION
        ) ?? 0;
        const levelForecasted: Reading | undefined = currentDateInSeconds ? getTankLevelForecast(
          lastSample,
          currentDateInSeconds,
          samplePoint.config?.maxValue,
          country
        ) : undefined;
        const level: Reading = {
          metric: {
            value: levelForecasted?.metric.value ?? lastSample.rwValue,
            unit: { symbol: LevelDisplayUnit.CM }
          },
          locale: {
            value: levelForecasted?.locale.value ?? levelActualLocale,
            unit: { symbol: levelSmSymbol }
          }
        };
        const volumeL: number = calculateTankVolumeL(
          lastSample.rwValue,
          samplePoint.config?.maxValue,
          samplePoint.config?.totalCapacity,
          samplePoint.config,
          { decimalPlaces: INTERMEDIARY_PRECISION }
        );
        const volumeLocale: number | undefined = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          volumeL,
          TANK_VOLUME_PRECISION
        );
        let capacity: number;
        if (samplePoint.config.totalCapacity) {
          capacity = percentage(
            volumeL,
            samplePoint.config.totalCapacity
          );
        } else {
          capacity = percentage(
            level.locale.value,
            samplePoint.config.maxValue
          );
        }
        selectedSamplePoint.config.maxValue = heightLocale;
        selectedSamplePoint.config.totalCapacity = maxVolumeLocale;
        selectedSamplePoint.fuelLevelStatistics = {
          changePerHour: getTankChangePerHour(lastSample, country),
          level: level.locale.value,
          capacity,
          height: heightLocale,
          volume: volumeLocale,
          maxVolume: maxVolumeLocale
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.LIQUID_FERTILISER_TANK) {
        const localeMaxValue = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          samplePoint.config.maxValue,
          TANK_LEVEL_PRECISION
        ) || 0;
        const localeTotalCapacity = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          samplePoint.config.totalCapacity,
          TANK_VOLUME_PRECISION
        );
        const localeLevel: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          lastSample.rwValue,
          TANK_LEVEL_PRECISION
        )!; // Won't be undefined because lastSample.rwValue is not undefined
        const forecastedLevel = currentDateInSeconds ? getTankLevelForecast(
          lastSample,
          currentDateInSeconds,
          samplePoint.config.maxValue,
          country
        ) : undefined;
        const volumeL = calculateTankVolumeL(
          lastSample.rwValue,
          samplePoint.config.maxValue,
          samplePoint.config.totalCapacity,
          samplePoint.config,
          { decimalPlaces: INTERMEDIARY_PRECISION }
        );
        const localeVolume: number = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          volumeL,
          TANK_VOLUME_PRECISION
        )!; // Won't be undefined because volumeL is not undefined
        const forecastedVolumeL: number | undefined = !isNil(forecastedLevel) ? calculateTankVolumeL(
          forecastedLevel.metric.value,
          samplePoint.config?.maxValue,
          samplePoint.config?.totalCapacity,
          samplePoint.config
        ) : undefined;
        const localeForecastedVolume = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          forecastedVolumeL,
          TANK_VOLUME_PRECISION
        );
        const volumePercentFull: number = percentage(
          forecastedVolumeL ?? volumeL,
          samplePoint.config.totalCapacity
        );
        const levelPercentFull: number = percentage(
          forecastedLevel?.metric.value ?? lastSample.rwValue,
          samplePoint.config.maxValue
        );
        selectedSamplePoint.liquidFertiliserStatistics = {
          level: forecastedLevel?.locale.value ?? localeLevel,
          height: localeMaxValue ?? 0,
          volume: localeForecastedVolume ?? localeVolume,
          maxVolume: localeTotalCapacity,
          capacity: samplePoint.config.totalCapacity ? volumePercentFull : levelPercentFull,
          changePerHour: getTankChangePerHour(lastSample, country)
        };
        selectedSamplePoint.config.maxValue = localeMaxValue;
        selectedSamplePoint.config.totalCapacity = localeTotalCapacity;
      }

      if (lastSample && assetTypeId === AssetTypeCode.PIPE) {
        const isGasPipe = samplePoint.sensorTags.id === SensorTagId.GAS_FLOW_METER;
        const flowRateUnitType = isGasPipe ? UnitType.FLOW_GAS : UnitType.FLOW;
        const volumeUnitType = isGasPipe ? UnitType.VOLUME_GAS : UnitType.VOLUME_SM;
        const aggregates = (
          samplePointsStatistic as SamplePointStatisticWithAggregates
        )?.aggregates;
        const aggregatesOneDay = convertMetricToImperialAlwaysRounding(
          volumeUnitType,
          country,
          aggregates?.oneDay?.values?.sum ?? undefined
        );
        const aggregatesSevenDays = convertMetricToImperialAlwaysRounding(
          volumeUnitType,
          country,
          aggregates?.sevenDays?.values?.sum ?? undefined
        );
        const aggregatesThirtyDays = convertMetricToImperialAlwaysRounding(
          volumeUnitType,
          country,
          aggregates?.thirtyDays?.values?.sum ?? undefined
        );
        const currentFlowRate = getSampleVolumePerMinute(lastSample);
        const currentFlowRateLocale = convertMetricToImperialAlwaysRounding(
          flowRateUnitType,
          country,
          currentFlowRate
        );
        selectedSamplePoint.flowStatistics = {
          current: currentFlowRateLocale,
          oneDay: roundNumber(aggregatesOneDay, { decimalPlaces: 0 }),
          sevenDays: roundNumber(aggregatesSevenDays, { decimalPlaces: 0 }),
          thirtyDays: roundNumber(aggregatesThirtyDays, { decimalPlaces: 0 })
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.LINE_PRESSURE) {
        const maxValue = selectedSamplePoint.config?.maxValue;
        if (maxValue !== undefined) {
          selectedSamplePoint.config.maxValue = convertMetricToImperial(
            UnitType.PRESSURE_LG,
            country,
            maxValue,
            LINE_PRESSURE_PRECISION
          );
        }
        const currentPressure = convertMetricToImperialAlwaysRounding(
          UnitType.PRESSURE_LG,
          country,
          lastSample.rwValue,
          LINE_PRESSURE_PRECISION
        ) ?? 0;
        selectedSamplePoint.linePressureStatistics = {
          currentCapacity:
            maxValue && maxValue > 0
              ? roundNumber((lastSample.rwValue * 100) / maxValue)
              : undefined,
          currentPressure
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.RAIN_GAUGE) {
        selectedSamplePoint.rainfallStatistics = getRainSummary(samplePointsStatistic, country);
      }

      if (lastSample && isWirelessTrough(samplePoint)) {
        const decimalPlaces = isUS ? 1 : 0;
        const aggregates = (
          samplePointsStatistic as SamplePointStatisticWithAggregates
        )?.aggregates;
        const aggregatesHighestAverageThisFortnight = convertMetricToImperial(
          UnitType.LEVEL_SM,
          country,
          aggregates?.thisFortnight.values.highestAverage ?? undefined
        );
        const aggregatesMinimumThisFortnight = convertMetricToImperial(
          UnitType.LEVEL_SM,
          country,
          aggregates?.thisFortnight?.values.lowestMinimum ?? undefined
        );

        selectedSamplePoint.config.maxValue =
          convertMetricToImperial(
            UnitType.LEVEL_SM,
            country,
            selectedSamplePoint.config.maxValue
          ) || 0;

        selectedSamplePoint.troughStatistics = {
          highestAverageThisFortnight: roundNumber(
            aggregatesHighestAverageThisFortnight,
            { decimalPlaces }
          ),
          minimumThisFortnight: roundNumber(aggregatesMinimumThisFortnight, {
            decimalPlaces
          }),
          latestAverage: convertMetricToImperial(
            UnitType.LEVEL_SM,
            country,
            lastSample.multiDimValues?.sampleDim?.find(
              (s) => s.dataType === DataType.AVERAGE
            )?.rwValue
          ),
          latestMinimum: convertMetricToImperial(
            UnitType.LEVEL_SM,
            country,
            lastSample.multiDimValues?.sampleDim?.find(
              (s) => s.dataType === DataType.MINIMUM
            )?.rwValue
          )
        };
      }

      if (lastSample && isTroughDirect(samplePoint)) {
        const configuredHeight = selectedSamplePoint.config.maxValue;
        const autoCalibratedHeight = lastSample.extraValues.maxLevel;

        // maxValue is default to 2 decimal places for later calculations (e.g., trigger value)
        selectedSamplePoint.config.maxValue = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          configuredHeight,
          2
        );
        selectedSamplePoint.config.maxValueAutoCalibrated = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_SM,
          country,
          autoCalibratedHeight,
          2
        );
        selectedSamplePoint.config.totalCapacity = convertMetricToImperialAlwaysRounding(
          UnitType.VOLUME_SM,
          country,
          selectedSamplePoint.config.totalCapacity,
          0
        );
        selectedSamplePoint.troughDirectStatistics = {
          level: {
            value: convertMetricToImperialAlwaysRounding(
              UnitType.LEVEL_SM,
              country,
              lastSample.rwValue,
              TROUGH_DIRECT_PRECISION) ?? 0,
            unit: {
              symbol: levelSmSymbol
            }
          },
          capacity: {
            value: percentage(
              lastSample.rwValue,
              configuredHeight ?? TROUGH_DIRECT_DEFAULT_HEIGHT_CM,
              { ceilingPercentage: 100 }
            ),
            unit: {
              symbol: '%'
            }
          }
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.SOIL) {
        const aggregateValues = (
          samplePointsStatistic as SamplePointStatisticWithAggregates
        ).aggregates?.thisFortnight?.values;

        selectedSamplePoint.soilStatistics = {
          maxMoistureThisFortnight: roundNumber(
            aggregateValues?.highestAverageMoisture ?? undefined
          ),
          minMoistureThisFortnight: roundNumber(
            aggregateValues?.lowestMinimumMoisture ?? undefined
          ),
          maxTempThisFortnight: roundNumber(
            aggregateValues?.highestAverageTemp ?? undefined
          ),
          minTempThisFortnight: roundNumber(aggregateValues?.lowestMinimumTemp ?? undefined),
          latestMoisture: roundNumber(
            lastSample.multiDimValues?.sampleDim?.find(
              (s) => s.dataType === RawAssetTypeCode.SOIL_MOISTURE
            )?.rwValue
          ),
          latestTemp: roundNumber(
            lastSample.multiDimValues?.sampleDim?.find(
              (s) => s.dataType === RawAssetTypeCode.SOIL_TEMP
            )?.rwValue
          )
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.DAM) {
        const {
          levelDisplayUnit,
          volumeDisplayUnit,
          liquidLevelMeasurementDirection,
          maxDepth: maxDepthCm
        } = selectedSamplePoint.config as DamSamplePoint['config'];
        const damIsAdvanced = selectedSamplePoint.config?.enabledVolumeMapping;
        const levelCm = lastSample.rwValue;
        const levelLocale = getDamLevel(
          levelCm,
          maxDepthCm,
          liquidLevelMeasurementDirection,
          levelDisplayUnit
        );
        const maxDepthQuantity: Quantity = { value: maxDepthCm ?? 0, unit: { symbol: LevelDisplayUnit.CM } };
        const maxDepthLocale: number = convertUnit(
          maxDepthQuantity,
          { symbol: levelDisplayUnit },
          DAM_PRECISION
        );
        const maxVolume =
          convertLitresTo(selectedSamplePoint.config?.maxVolume, volumeDisplayUnit) ?? 0;
        const simpleDamVolume = maxDepthCm ? maxVolume * (levelCm / maxDepthCm) : 0;
        const advancedDamVolume =
          convertLitresTo(lastSample?.volume, volumeDisplayUnit) ?? 0;
        let capacity: number;

        if (damIsAdvanced && maxVolume) {
          capacity = percentage(advancedDamVolume, maxVolume);
        } else {
          capacity = maxDepthCm ? percentage(levelCm, maxDepthCm) : 0;
        }
        const reducedLevelUnit = getUnitSymbolByCountry({
          unitType: UnitType.LEVEL_MD,
          country
        }) as UnitLevelMd;
        const reducedLevelLocale = convertUnit({
          value: lastSample?.levelRl ?? 0,
          unit: { symbol: LevelDisplayUnit.M }
        },
          { symbol: reducedLevelUnit },
          DAM_PRECISION
        );
        selectedSamplePoint.damStatistics = {
          changePerHour: getTankChangePerHour(lastSample, country, UnitType.TREND_MD),
          level: levelLocale ?? 0,
          volume: damIsAdvanced ? advancedDamVolume : simpleDamVolume,
          maxVolume,
          maxDepth: maxDepthLocale,
          capacity,
          reducedLevel: damIsAdvanced ? reducedLevelLocale : undefined
        };
      }

      if (!isNil(lastSample) && assetTypeId === AssetTypeCode.BORE) {
        const levelLocale: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_MD,
          country,
          lastSample.rwValue,
          BORE_LEVEL_PRECISION
        )!;
        const maxDepthLocale: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_MD,
          country,
          selectedSamplePoint.config.maxDepth,
          BORE_LEVEL_PRECISION
        )!;
        const totalBoreDepthLocale: number = convertMetricToImperialAlwaysRounding(
          UnitType.LEVEL_MD,
          country,
          selectedSamplePoint.config.totalBoreDepth,
          BORE_LEVEL_PRECISION
        )!;
        selectedSamplePoint.boreStatistics = {
          level: getBoreDepthLevelDisplay(levelLocale)
        };
        selectedSamplePoint.config.maxDepth = maxDepthLocale;
        selectedSamplePoint.config.totalBoreDepth = totalBoreDepthLocale;
      }

      if (lastSample && assetTypeId === AssetTypeCode.SAFETY_CHECK_IN) {
        let sosDate;
        const sosStatistics = (
          samplePointsStatistic as MergedSamplePointStatistic
        )._hidden;
        if (sosStatistics) {
          sosDate = sosStatistics.lastSample?.date;
        }
        selectedSamplePoint.safetyCheckInStatistics = {
          checkInDate: lastSample?.date,
          sosDate
        };
      }

      if (
        samplePointsStatistic &&
        assetTypeId === AssetTypeCode.MACHINE_CONTROL
      ) {
        let connectedSamplePointCount: MachineControlStatistic['connectedSamplePointCount'] = 0;
        if ((samplePointsStatistic as MachineControlStatistic).sourceSamplePoint?.sid) {
          connectedSamplePointCount++;
        }
        if ((samplePointsStatistic as MachineControlStatistic).destinationSamplePoint?.sid) {
          connectedSamplePointCount++;
        }
        const parsedStatus = {
          [MachineControlStatus.OFF]: 'Power is off',
          [MachineControlStatus.ON]: 'Power is on',
          [MachineControlStatus.TURNING_OFF]: 'Turning off',
          [MachineControlStatus.TURNING_ON]: 'Turning on',
          [MachineControlStatus.ERROR]: 'Error'
        };
        const parsedVerboseAutomation = {
          [AutomaticControlType.MANUAL]: 'Manual',
          [AutomaticControlType.AUTO]: 'Auto Enabled',
          [AutomaticControlType.AUTO_PAUSED]: 'Auto Paused'
        };
        selectedSamplePoint.machineControlStatistics = {
          ...(samplePointsStatistic as MachineControlStatistic),
          connectedSamplePointCount: connectedSamplePointCount as MachineControlStatistic['connectedSamplePointCount'],
          verboseStatus:
            parsedStatus[
            (samplePointsStatistic as MachineControlStatistic).status ??
            MachineControlStatus.ERROR
            ],
          verboseAutomation:
            parsedVerboseAutomation[
            (samplePointsStatistic as MachineControlStatistic).automation
            ]
        };
      }

      if (lastSample && assetTypeId === AssetTypeCode.POWER_METER) {
        try {
          assertIsPowerMeterStatistic(samplePointsStatistic);
          selectedSamplePoint.powerStatistics = computePowerMeterSummaryMetrics(samplePointsStatistic);
        } catch (error) {
          // No action needed. samplePointsSelectors often execute before statisticsParsers, leading to expected errors.
          // We can safely skip error reporting here, as the selectors will re-execute once the statistics are correctly
          // parsed.
        }
      }

      if (lastSample && assetTypeId === AssetTypeCode.WEATHER_STATION_CONTROLLER) {
        selectedSamplePoint.weatherStatistics = retrieveWeatherSummaryMetrics(
          samplePoint,
          samplePointsStatistics,
          country
        );
      }

      // ============================== HEATER ADD-ON STATISTICS ==============================
      const heaterAddOn = (samplePoint as SamplePointWithHeaterAddOn)._hidden?.heater;
      if (heaterAddOn) {
        const onOffSamplePointId = heaterAddOn.onOff;
        const onOffStat = onOffSamplePointId ? samplePointsStatistics[onOffSamplePointId] : undefined;

        const temperatureSamplePointId = heaterAddOn.temperature;
        const temperatureStat = temperatureSamplePointId ? samplePointsStatistics[temperatureSamplePointId] : undefined;
        const temperatureSummary = temperatureStat
          ? getCurrentLowHighSummary(AssetTypeCode.TEMPERATURE, temperatureStat, country)
          : undefined;

        selectedSamplePoint.heaterStatistics = {
          onOffReading: getHeaterStatus(onOffStat?.lastSample?.rwValue),
          temperatureLocale: temperatureSummary?.current ?? null
        };
      }

      return selectedSamplePoint;
    })
);

const selectSamplePointsAsArray = createSelector(
  selectSamplePoints,
  (samplePoints) => orderBy(Object.values(samplePoints), 'name')
);

const makeSelectSamplePointsByDevice = (deviceId: number) =>
  createSelector(selectSamplePoints, (samplePoints) =>
    Object.values(samplePoints).filter((s) => s.device?.id === deviceId)
  );

const selectSamplePointsGroupedByAssetTypeId = createDeepEqualSelector(
  selectSamplePointsAsArray,
  (samplePointsAsArray) => groupBy(samplePointsAsArray, 'assetTypeId')
);

const makeSelectSamplePointsByAssetTypeId = (assetTypeId: AssetTypeId) =>
  createSelector(
    selectSamplePointsGroupedByAssetTypeId,
    (samplePointsGroupedByAssetTypeId) =>
      samplePointsGroupedByAssetTypeId[assetTypeId] || []
  );

const selectSamplePointsGroupedByAssetTypeIdForDefaultSite = createSelector(
  [selectSamplePointsGroupedByAssetTypeId, selectDefaultSite],
  (samplePointsGroupedByAssetTypeId, defaultSite) =>
    mapValues(samplePointsGroupedByAssetTypeId, (samplePoints) =>
      samplePoints.filter(({ siteId }) => siteId === defaultSite?.id)
    )
);

const makeSelectSamplePointsByAssetTypeIdForDefaultSite = (
  assetTypeId: AssetTypeId
) =>
  createSelector(
    [makeSelectSamplePointsByAssetTypeId(assetTypeId), selectDefaultSite],
    (samplePoints, defaultSite) =>
      samplePoints.filter(({ siteId }) => siteId === defaultSite?.id) || []
  );

const makeSelectSamplePointsByAssetTypeIdsForDefaultSite = (assetTypeIds: AssetTypeId[]) =>
  createSelector(
    selectSamplePointsGroupedByAssetTypeIdForDefaultSite,
    (samplePointsGroupedByAssetTypeId) => {
      let samplePoints: SamplePoint[] = [];
      assetTypeIds.forEach((assetTypeId) => {
        samplePoints = [
          ...samplePoints,
          ...(samplePointsGroupedByAssetTypeId[assetTypeId] || [])
        ];
      });

      return orderBy(samplePoints, 'name');
    }
  );

/**
 * Get the sample point straight from store
 * TODO: FMBT-4049 add internal sample points into sample point state.
 */
const makeSelectRawSamplePointById = (id?: SamplePointId) =>
  createSelector(selectSamplePointsState, (samplePoints) => id ? samplePoints[id] : undefined);

const makeSelectSamplePointById = (samplePointId: SamplePointId) =>
  createSelector(
    selectSamplePoints,
    (samplePoints): SamplePoint | undefined => {
      const topLevelSamplePoint: SamplePoint | undefined = samplePoints[samplePointId];
      if (topLevelSamplePoint) return topLevelSamplePoint;
      return findNestedWeatherSensors(Object.values(samplePoints), 'id', samplePointId)[0];
    }
  );

/**
 * Checks if a sample point ID belongs to a heater host.
 */
const makeSelectHasHeaterHost = (nestedSamplePointId: SamplePointId) =>
  createSelector(
    selectSamplePoints,
    (samplePoints): boolean =>
      hasHeaterHost(Object.values(samplePoints), nestedSamplePointId)
  );

// TODO: we shouldn't memoize basic finds, only when there are mutations
const makeSelectSamplePointBySid = (sid: string) =>
  createSelector(selectSamplePointsAsArray, (samplePoints): SamplePoint | undefined => {
    const topLevelSamplePoint: SamplePoint | undefined = samplePoints.find((sp) => sp.sid === sid);
    if (topLevelSamplePoint) return topLevelSamplePoint;
    return findNestedWeatherSensors(samplePoints, 'sid', sid)[0];
  });

/**
 * Returns sample points that have these sids, ordered by
 * the sids passed in as argument
 */
const makeSelectSamplePointsBySids = (sids: SamplePointSid[]) =>
  createSelector(selectSamplePointsAsArray, (samplePoints): SamplePoint[] => {
    const result: SamplePoint[] = [];
    sids.forEach(sid => {
      const topLevelSamplePoint: SamplePoint | undefined = samplePoints.find((sp) => sp.sid === sid);
      if (topLevelSamplePoint) result.push(topLevelSamplePoint);
      const nestedWeatherSensor: SamplePoint | undefined = findNestedWeatherSensors(samplePoints, 'sid', sid)[0];
      if (nestedWeatherSensor) result.push(nestedWeatherSensor);
    });
    return result;
  });

const makeSelectPumpControlsByConnectedSids =
  (sids: string[]) => (state: ApplicationRootState) => {
    return Object.values(state.samplePoints)
      .filter((sp) => {
        const machineControlStatistics = (sp as MachineControl).machineControlStatistics;
        if (!machineControlStatistics) return false;
        const isPumpControlWithConnectedSamplePoints = sp.assetTypeId === AssetTypeCode.MACHINE_CONTROL
          && machineControlStatistics.connectedSamplePointCount > 0;
        if (isPumpControlWithConnectedSamplePoints) {
          return sids.some((sid) =>
            sid === machineControlStatistics.destinationSamplePoint?.sid
            || sid === machineControlStatistics.sourceSamplePoint?.sid
          );
        }
        return false;
      }
      );
  };

const makeSelectSamplePointsCoordinates = (siteId: SiteId | null) =>
  createDeepEqualSelector(selectSamplePointsAsArray, (samplePointsAsArray) => {
    const resultWithPossibleDups = samplePointsAsArray
      .filter((sp) => siteId === null || sp.siteId === siteId)
      .map(({ location }) => location?.coordinates)
      .filter((coordinates): coordinates is LatLong => Boolean(coordinates));

    const uniqueResults = uniqWith(
      resultWithPossibleDups,
      ([x, y], [x2, y2]) => x === x2 && y === y2
    );

    return orderBy(uniqueResults, '[1]', 'desc');
  });

const selectSamplePointsGroupedByLocation =
  createDeepEqualSelector(selectSamplePointsAsArray, (samplePointsAsArray) =>
    groupBy(samplePointsAsArray, 'location.coordinates')
  );

const makeSelectSamplePointsByLocation = (coordinatesString: string) =>
  createDeepEqualSelector(
    selectSamplePointsGroupedByLocation,
    (groupedSamplePoints) => groupedSamplePoints[coordinatesString] as SamplePointWithNonNullableLocation[] || []
  );

const makeSelectIsAdvancedDam = (samplePointId: SamplePointId) =>
  createSelector(
    selectSamplePoints,
    (samplePoints) => samplePoints[samplePointId]?.config?.enabledVolumeMapping ?? false
  );

export {
  makeSelectHasHeaterHost,
  makeSelectIsAdvancedDam,
  makeSelectPumpControlsByConnectedSids,
  makeSelectRawSamplePointById,
  makeSelectSamplePointById,
  makeSelectSamplePointBySid,
  makeSelectSamplePointsByAssetTypeId,
  makeSelectSamplePointsByAssetTypeIdForDefaultSite,
  makeSelectSamplePointsByAssetTypeIdsForDefaultSite,
  makeSelectSamplePointsByDevice,
  makeSelectSamplePointsByLocation,
  makeSelectSamplePointsBySids,
  makeSelectSamplePointsCoordinates,
  selectSamplePoints,
  selectSamplePointsAsArray,
  selectSamplePointsGroupedByAssetTypeIdForDefaultSite,
  selectSamplePointsState
};
